import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useI18n, LOCALES } from "@/contexts/I18nContext";
import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  User, Palette, Languages, ShieldCheck, Building2, KeyRound, Sun, Moon,
  Monitor, FileText, ArrowUpRight, Sparkles, Download, Trash2, Lock, Loader2,
} from "lucide-react";
import { Input } from "@/components/ui/input";

function Card({ title, hint, icon: Icon, children, testid }) {
  return (
    <section data-testid={testid} className="yl-surface yl-beam p-6 lg:p-7">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ background: "hsl(var(--cyan) / .12)" }}>
          <Icon className="w-4 h-4" style={{ color: "hsl(var(--ink-info))" }} strokeWidth={2} />
        </div>
        <div className="min-w-0">
          <h2 className="font-heading text-lg">{title}</h2>
          {hint && <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{hint}</p>}
        </div>
      </div>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-border last:border-0">
      <div className="eyebrow text-[9px] text-muted-foreground">{label}</div>
      <div className={`text-sm font-semibold text-right truncate ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
    </div>
  );
}

const THEMES = [
  { k: "light", l: "Light", icon: Sun },
  { k: "dark", l: "Dark", icon: Moon },
];

function PrivacyCard() {
  const { user, logout } = useAuth();
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState("");
  const [armed, setArmed] = useState(false);

  async function exportData() {
    setBusy("export");
    try {
      const res = await api.get("/me/data-export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "meetiq360-my-data.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data export has downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  }

  async function erase() {
    setBusy("erase");
    try {
      await api.post("/me/erase", { confirm_email: confirm, delete_my_meetings: true });
      toast.success("Your account and its data have been erased");
      setTimeout(logout, 1200);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  }

  return (
    <Card testid="settings-privacy" title="Privacy & your data" icon={Lock}
      hint="Your rights under the GDPR and India's DPDP Act, 2023 — exercised here, not by email.">
      <button
        type="button"
        data-testid="settings-export-data"
        onClick={exportData}
        disabled={!!busy}
        className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-full border border-border bg-card text-sm font-bold hover:text-primary hover:border-primary/50 transition-colors disabled:opacity-60"
      >
        {busy === "export" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" strokeWidth={2.2} />}
        Download everything we hold about you
      </button>

      <div className="mt-5 rounded-2xl border border-destructive/30 p-4">
        <div className="eyebrow text-[9px] text-destructive">Erase my account</div>
        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
          Permanently deletes your profile, the meetings you organised and their recordings, transcripts, tasks,
          decisions and share links. Invoices are retained for the statutory tax period. This cannot be undone.
        </p>
        {!armed ? (
          <button
            type="button"
            data-testid="settings-erase-arm"
            onClick={() => setArmed(true)}
            className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-full border border-destructive/40 text-sm font-bold text-destructive hover:bg-destructive/10 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={2.2} /> Start erasure
          </button>
        ) : (
          <div className="mt-3 space-y-3">
            <Input
              data-testid="settings-erase-confirm"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={`Type ${user?.email} to confirm`}
              className="h-10 rounded-xl"
            />
            <button
              type="button"
              data-testid="settings-erase-confirm-btn"
              onClick={erase}
              disabled={busy === "erase" || confirm.trim().toLowerCase() !== (user?.email || "").toLowerCase()}
              className="w-full h-10 rounded-full bg-destructive text-destructive-foreground text-sm font-bold disabled:opacity-50"
            >
              {busy === "erase" ? "Erasing…" : "Erase my account permanently"}
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mt-5">
        {[
          { l: "Privacy Policy", to: "/legal/privacy" },
          { l: "Terms", to: "/legal/terms" },
          { l: "DPA", to: "/legal/dpa" },
          { l: "Cookies", to: "/legal/cookies" },
        ].map((x) => (
          <Link
            key={x.to}
            to={x.to}
            data-testid={`settings-legal-${x.to.split("/").pop()}`}
            className="rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-semibold hover:text-primary hover:border-primary/50 transition-colors"
          >
            {x.l}
          </Link>
        ))}
      </div>
    </Card>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const { locale, setLocale } = useI18n();

  return (
    <div data-testid="settings-root" className="p-6 md:p-8 lg:p-12 space-y-10 max-w-[1200px]">
      <header className="yl-fade-up">
        <div className="eyebrow">Account · preferences</div>
        <h1 className="font-heading text-4xl sm:text-5xl mt-3 leading-none">Settings.</h1>
        <p className="text-sm md:text-base text-muted-foreground mt-3 max-w-xl leading-relaxed">
          Your profile, how the workspace looks, and the language it speaks.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card testid="settings-profile" title="Profile" hint="Signed in to this workspace." icon={User}>
          <div className="flex items-center gap-4 mb-5">
            <div
              className="w-14 h-14 rounded-2xl grid place-items-center text-base font-bold text-white shrink-0"
              style={{ background: "linear-gradient(135deg, hsl(var(--navy)), hsl(var(--cyan)))" }}
            >
              {(user?.name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-heading text-xl truncate">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>
          <Row label="Role" value={user?.role} />
          <Row label="Org role" value={user?.org_role || "Member"} />
          <Row label="Department" value={user?.department || "—"} />
          <Row label="Designation" value={user?.designation || "—"} />
        </Card>

        <Card testid="settings-appearance" title="Appearance" hint="Applies instantly and is remembered on this device." icon={Palette}>
          <div className="grid grid-cols-2 gap-3">
            {THEMES.map((t) => (
              <button
                key={t.k}
                type="button"
                data-testid={`settings-theme-${t.k}`}
                onClick={() => setTheme(t.k)}
                className={`rounded-2xl border p-4 text-left transition-colors ${
                  theme === t.k ? "border-primary bg-secondary/60" : "border-border hover:border-primary/40"
                }`}
              >
                <t.icon className="w-4 h-4 text-primary" strokeWidth={2} />
                <div className="text-sm font-bold mt-3">{t.l}</div>
                <div className="font-mono text-[10px] text-muted-foreground mt-1 uppercase tracking-wider">
                  {theme === t.k ? "Active" : "Select"}
                </div>
              </button>
            ))}
          </div>
          <div className="mt-5 flex items-start gap-2.5 rounded-2xl bg-secondary/50 p-4">
            <Monitor className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" strokeWidth={2} />
            <p className="text-xs text-muted-foreground leading-relaxed">
              First visit follows your operating-system preference. Press{" "}
              <kbd className="font-mono text-[10px] border border-border rounded px-1 py-0.5">⌘K</kbd> anywhere and type
              “theme” to switch without leaving the page.
            </p>
          </div>
        </Card>

        <Card testid="settings-language" title="Language" hint="Interface language for navigation and chrome. Meeting content stays in its original language." icon={Languages}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {LOCALES.map((l) => (
              <button
                key={l.code}
                type="button"
                data-testid={`settings-locale-${l.code}`}
                onClick={() => setLocale(l.code)}
                className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${
                  locale === l.code ? "border-primary bg-secondary/60" : "border-border hover:border-primary/40"
                }`}
              >
                <div className="font-mono text-[9px] text-muted-foreground uppercase tracking-wider">{l.flag}</div>
                <div className="text-sm font-semibold mt-1 truncate">{l.native}</div>
              </button>
            ))}
          </div>
        </Card>

        <Card testid="settings-security" title="Security" hint="Password and session controls." icon={ShieldCheck}>
          <Row label="Account status" value={user?.is_active === false ? "Suspended" : "Active"} />
          <Row label="Meeting quota" value={user?.meeting_quota ?? "Unlimited"} />
          <Row label="Valid until" value={user?.valid_until || "No expiry"} mono />
          <Link
            to="/change-password"
            data-testid="settings-change-password"
            className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-full bg-primary text-primary-foreground text-sm font-bold hover:bg-[hsl(var(--primary-hover))] transition-colors"
          >
            <KeyRound className="w-3.5 h-3.5" strokeWidth={2.2} />
            Change password
          </Link>
        </Card>

        <Card testid="settings-workspace" title="Workspace & plan" hint="Billing is managed per organisation." icon={Building2}>
          <Row label="Organisation" value={user?.org_name || user?.org_id || "—"} />
          <Row label="Plan" value={user?.plan_name || user?.plan || "Trial"} />
          <Link
            to="/billing"
            data-testid="settings-billing-link"
            className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-full border border-border bg-card text-sm font-bold hover:text-primary hover:border-primary/50 transition-colors"
          >
            Manage billing
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.2} />
          </Link>
        </Card>

        <PrivacyCard />

        <Card testid="settings-about" title="About MeetIQ360" hint="Built by Yanthra Labs." icon={Sparkles}>
          <p className="text-sm text-muted-foreground leading-relaxed">
            MeetIQ360 records meetings, transcribes them in 31 languages, validates the transcript before any AI reads
            it, then extracts decisions, action items, owners, due dates, risks and open questions — and drives them to
            completion through an approval register and an execution board.
          </p>
          <a
            href="/AGENTIC_AVATAR_PORTING_SPEC.pdf"
            target="_blank"
            rel="noreferrer"
            data-testid="settings-spec-link"
            className="mt-5 inline-flex items-center gap-2 h-10 px-4 rounded-full border border-border bg-card text-sm font-bold hover:text-primary hover:border-primary/50 transition-colors"
          >
            <FileText className="w-3.5 h-3.5" strokeWidth={2.2} />
            Technical spec (PDF)
          </a>
        </Card>
      </div>
    </div>
  );
}
